import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';
import { getEffectiveScore } from '../../utils/scoreSourceUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Building2,
  FileText,
  Target,
  Calendar,
  Search,
  Plus,
  ChevronRight,
  Loader2,
  DollarSign,
  Users,
  Clock,
  TrendingUp,
  AlertCircle,
  ArrowRight,
  Briefcase,
  LayoutGrid,
  List,
  Filter,
  ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const getStatusStyle = (status) => {
  const styles = {
    'INTAKE': 'bg-slate-100 text-slate-700 border-slate-200',
    'QUALIFICATION': 'bg-blue-100 text-blue-700 border-blue-200',
    'STRATEGY': 'bg-purple-100 text-purple-700 border-purple-200',
    'DRAFTING': 'bg-amber-100 text-amber-700 border-amber-200',
    'SUBMITTED': 'bg-teal-100 text-teal-700 border-teal-200',
    'WON': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'LOST': 'bg-red-100 text-red-700 border-red-200',
    'NO_BID': 'bg-slate-200 text-slate-600 border-slate-300',
    'ARCHIVED': 'bg-slate-200 text-slate-500 border-slate-300'
  };
  return styles[status] || 'bg-slate-100 text-slate-600 border-slate-200';
};

// Format currency
const formatCurrency = (value) => {
  if (!value) return '$0';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

// Format deadline
const formatDeadline = (dateStr) => {
  if (!dateStr) return { text: 'TBD', urgent: false };
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { text: 'Overdue', urgent: true };
  if (diffDays === 0) return { text: 'Today', urgent: true };
  if (diffDays === 1) return { text: 'Tomorrow', urgent: true };
  if (diffDays <= 7) return { text: `${diffDays} days`, urgent: true };
  
  return { 
    text: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
    urgent: false 
  };
};

// pWin color helper
const getPwinColor = (score) => {
  if (score >= 70) return 'text-emerald-600 bg-emerald-50';
  if (score >= 50) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
};

export default function ConsultantClientDashboard() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { user, authToken, getAuthHeaders } = useAuth();
  const { enterPursuit } = usePursuit();

  // State
  const [loading, setLoading] = useState(true);
  const [pursuits, setPursuits] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'list'

  // Fetch all data
  useEffect(() => {
    if (authToken) {
      fetchData();
    }
  }, [authToken]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch pursuits (API already returns all accessible pursuits)
      const pursuitsRes = await fetch(`${API_URL}/api/pursuits`, {
        headers: getAuthHeaders()
      });
      
      let pursuitsList = [];
      if (pursuitsRes.ok) {
        const pursuitsData = await pursuitsRes.json();
        pursuitsList = Array.isArray(pursuitsData) ? pursuitsData : (pursuitsData.pursuits || []);
        setPursuits(pursuitsList);
      }

      // Fetch linked clients
      const consultantOrgId = user?.organizationId;
      if (consultantOrgId) {
        const clientsRes = await fetch(
          `${API_URL}/api/clients?consultant_org_id=${consultantOrgId}`,
          { headers: getAuthHeaders() }
        );
        
        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData.items || []);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Group pursuits by client organization
  const pursuitsByClient = useMemo(() => {
    const grouped = {};
    
    pursuits.forEach(pursuit => {
      const clientId = pursuit.organization_id || 'unknown';
      const clientName = pursuit.client_name || pursuit.organization_name || 'Unknown Client';
      
      if (!grouped[clientId]) {
        grouped[clientId] = {
          id: clientId,
          name: clientName,
          pursuits: [],
          totalValue: 0,
          urgentCount: 0
        };
      }
      
      grouped[clientId].pursuits.push(pursuit);
      grouped[clientId].totalValue += parseFloat(pursuit.estimated_value) || 0;
      
      // Check for urgent deadlines
      if (pursuit.response_deadline) {
        const daysLeft = Math.ceil((new Date(pursuit.response_deadline) - new Date()) / (1000 * 60 * 60 * 24));
        if (daysLeft >= 0 && daysLeft <= 7) {
          grouped[clientId].urgentCount++;
        }
      }
    });
    
    return Object.values(grouped).sort((a, b) => b.pursuits.length - a.pursuits.length);
  }, [pursuits]);

  // Filter pursuits based on search and selected client
  const filteredPursuits = useMemo(() => {
    let filtered = pursuits;
    
    // Filter by client
    if (selectedClient !== 'all') {
      filtered = filtered.filter(p => p.organization_id === selectedClient);
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.opportunity_name?.toLowerCase().includes(query) ||
        p.agency_code?.toLowerCase().includes(query) ||
        p.solicitation_number?.toLowerCase().includes(query) ||
        p.client_name?.toLowerCase().includes(query) ||
        p.organization_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [pursuits, selectedClient, searchQuery]);

  // Calculate aggregate stats
  const stats = useMemo(() => {
    const activePursuits = pursuits.filter(p => !['WON', 'LOST', 'NO_BID', 'ARCHIVED'].includes(p.status));
    const totalValue = activePursuits.reduce((sum, p) => sum + (parseFloat(p.estimated_value) || 0), 0);
    const urgentDeadlines = activePursuits.filter(p => {
      if (!p.response_deadline) return false;
      const days = Math.ceil((new Date(p.response_deadline) - new Date()) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 7;
    }).length;
    const avgFitScore = activePursuits.length > 0
      ? Math.round(activePursuits.reduce((sum, p) => sum + (p.pwin_score || 0), 0) / activePursuits.length)
      : 0;

    // v3.11.19 — Display "Avg Fit Score" should mirror the per-pursuit
    // Fit Score shown elsewhere (Pipeline cards, GoNoGo Verdict). Use the
    // source-aware helper so we only count pursuits where AI analysis has
    // actually run; pursuits in PREREQUISITES_INCOMPLETE / ANALYSIS_REQUIRED
    // states return null and are excluded from BOTH numerator and
    // denominator. Previously we summed raw `pwin_score` (which can be a
    // stale composite or 0) across all active pursuits, producing
    // averages that didn't match what users saw on each pursuit card.
    const scored = activePursuits
      .map((p) => getEffectiveScore(p).score)
      .filter((s) => typeof s === 'number');
    const avgFitScoreEffective = scored.length > 0
      ? Math.round(scored.reduce((sum, s) => sum + s, 0) / scored.length)
      : null;
    const avgFitScoreCount = scored.length;

    return {
      totalClients: pursuitsByClient.length,
      activePursuits: activePursuits.length,
      totalValue,
      urgentDeadlines,
      avgFitScore,
      avgFitScoreEffective,
      avgFitScoreCount,
    };
  }, [pursuits, pursuitsByClient]);

  // Handle pursuit click - context switch into client environment
  const handlePursuitClick = (pursuit) => {
    enterPursuit({
      id: pursuit.id,
      title: pursuit.opportunity_name,
      rfpNumber: pursuit.solicitation_number,
      client: pursuit.client_name || pursuit.organization_name,
      notice_type: pursuit.notice_type,  // v3.13.0 — sidebar gates Capability Statement on this
    });
    navigate(`/${workspaceId}/pursuit/${pursuit.id}/intake`);
  };

  // Handle client card click - navigate to client's pursuit list
  const handleClientClick = (clientId) => {
    setSelectedClient(clientId);
    setViewMode('list');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading your client portfolio...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="consultant-client-dashboard" className="min-h-screen bg-slate-50">
      {/* Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm text-slate-500 mb-1">Consultant Portal</p>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Client Portfolio Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage pursuits across all your client organizations
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search pursuits, clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-72 border-slate-200"
                data-testid="dashboard-search"
              />
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate(`/${workspaceId}/pipeline`)}
              data-testid="new-pursuit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Pursuit
            </Button>
          </div>
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-5 gap-6">
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-clients">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalClients}</p>
                  <p className="text-xs text-slate-500">Clients w/ Active Pursuits</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-pursuits">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.activePursuits}</p>
                  <p className="text-xs text-slate-500">Active Pursuits</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-value">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.totalValue)}</p>
                  <p className="text-xs text-slate-500">Pipeline Value</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-urgent">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  stats.urgentDeadlines > 0 ? "bg-red-100" : "bg-slate-100"
                )}>
                  <Clock className={cn(
                    "w-5 h-5",
                    stats.urgentDeadlines > 0 ? "text-red-600" : "text-slate-600"
                  )} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.urgentDeadlines}</p>
                  <p className="text-xs text-slate-500">Urgent (7 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-fit">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="avg-fit-score-value">
                    {stats.avgFitScoreEffective !== null ? `${stats.avgFitScoreEffective}%` : '—'}
                  </p>
                  <p className="text-xs text-slate-500">
                    Avg Fit Score
                    {stats.avgFitScoreEffective !== null
                      ? ` · ${stats.avgFitScoreCount}/${stats.activePursuits} analyzed`
                      : ' · No AI analysis yet'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* View Toggle & Filter */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
              {viewMode === 'grouped' ? 'CLIENT OVERVIEW' : 'ALL PURSUITS'}
            </h2>
            {selectedClient !== 'all' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSelectedClient('all'); setViewMode('grouped'); }}
                className="text-xs"
              >
                Clear Filter
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grouped' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grouped')}
              className={viewMode === 'grouped' ? 'bg-blue-600' : ''}
              data-testid="view-grouped-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              By Client
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-blue-600' : ''}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-1" />
              All Pursuits
            </Button>
          </div>
        </div>

        {/* Grouped View - Client Cards */}
        {viewMode === 'grouped' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {pursuitsByClient.length === 0 ? (
              <Card className="col-span-full border-slate-200 bg-white shadow-sm">
                <CardContent className="p-12 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Pursuits Yet</h3>
                  <p className="text-slate-500 mb-4">Start by creating a pursuit for one of your clients</p>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => navigate(`/${workspaceId}/pipeline`)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Pursuit
                  </Button>
                </CardContent>
              </Card>
            ) : (
              pursuitsByClient.map((client) => (
                <Card
                  key={client.id}
                  className="border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleClientClick(client.id)}
                  data-testid={`client-card-${client.id}`}
                >
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center border border-blue-100">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base font-semibold text-slate-900">
                            {client.name}
                          </CardTitle>
                          <p className="text-xs text-slate-500">
                            {client.pursuits.length} pursuit{client.pursuits.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      {client.urgentCount > 0 && (
                        <Badge className="bg-red-100 text-red-700 border border-red-200">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          {client.urgentCount} urgent
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {/* Client Stats */}
                    <div className="flex items-center justify-between mb-4 text-sm">
                      <div>
                        <span className="text-slate-500">Pipeline:</span>
                        <span className="ml-2 font-semibold text-slate-900">
                          {formatCurrency(client.totalValue)}
                        </span>
                      </div>
                    </div>

                    {/* Recent Pursuits Preview */}
                    <div className="space-y-2">
                      {client.pursuits.slice(0, 3).map((pursuit) => {
                        const deadline = formatDeadline(pursuit.response_deadline);
                        return (
                          <div
                            key={pursuit.id}
                            className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePursuitClick(pursuit);
                            }}
                          >
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {pursuit.opportunity_name}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge className={cn("text-xs border", getStatusStyle(pursuit.status))}>
                                  {pursuit.status?.replace('_', ' ')}
                                </Badge>
                                {pursuit.agency_code && (
                                  <span className="text-xs text-slate-500">{pursuit.agency_code}</span>
                                )}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={cn(
                                "text-xs font-medium",
                                deadline.urgent ? "text-red-600" : "text-slate-600"
                              )}>
                                {deadline.text}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* View All Button */}
                    {client.pursuits.length > 3 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        View all {client.pursuits.length} pursuits
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* List View - All Pursuits */}
        {viewMode === 'list' && (
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-0">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <div className="col-span-2">Client</div>
                <div className="col-span-3">Opportunity</div>
                <div className="col-span-2">Agency</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1 text-center">Fit</div>
                <div className="col-span-2 text-right">Deadline</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-slate-100">
                {filteredPursuits.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No pursuits found</p>
                  </div>
                ) : (
                  filteredPursuits.map((pursuit) => {
                    const deadline = formatDeadline(pursuit.response_deadline);
                    return (
                      <div
                        key={pursuit.id}
                        className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors items-center"
                        onClick={() => handlePursuitClick(pursuit)}
                        data-testid={`pursuit-row-${pursuit.id}`}
                      >
                        {/* Client */}
                        <div className="col-span-2 flex items-center gap-2">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-slate-500" />
                          </div>
                          <span className="font-medium text-slate-900 text-sm truncate">
                            {pursuit.client_name || pursuit.organization_name || 'Client'}
                          </span>
                        </div>

                        {/* Opportunity */}
                        <div className="col-span-3">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {pursuit.opportunity_name}
                          </p>
                          <p className="text-xs text-slate-500">{pursuit.solicitation_number}</p>
                        </div>

                        {/* Agency */}
                        <div className="col-span-2">
                          <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">
                            {pursuit.agency_code || 'N/A'}
                          </Badge>
                        </div>

                        {/* Status */}
                        <div className="col-span-2">
                          <Badge className={cn("text-xs font-medium border", getStatusStyle(pursuit.status))}>
                            {pursuit.status?.replace('_', ' ')}
                          </Badge>
                        </div>

                        {/* Fit Score */}
                        <div className="col-span-1 flex justify-center">
                          {(() => {
                            // v3.11.21 — source-aware: render dash when AI
                            // analysis hasn't run, mirroring the indicator on
                            // the Contractor Pursuit Command Center.
                            const fit = getEffectiveScore(pursuit).score;
                            if (fit === null || fit === undefined) {
                              return (
                                <span
                                  className="text-xs font-semibold px-2 py-1 rounded bg-slate-50 border border-dashed border-slate-300 text-slate-400"
                                  title="Run AI Analysis to calculate Fit Score"
                                >
                                  —
                                </span>
                              );
                            }
                            return (
                              <span className={cn(
                                "text-sm font-bold px-2 py-1 rounded",
                                getPwinColor(fit)
                              )}>
                                {fit}%
                              </span>
                            );
                          })()}
                        </div>

                        {/* Deadline */}
                        <div className="col-span-2 text-right">
                          <p className={cn(
                            "font-medium text-sm",
                            deadline.urgent ? "text-red-600" : "text-slate-700"
                          )}>
                            {deadline.text}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </div>
  );
}
